import { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Mail, MessageSquare, Send, Globe, Presentation, Video, Image, CalendarRange } from 'lucide-react';
import type { TabType } from '../../types/results';

interface TabNavigationProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  emailCount: number;
  socialCount: number;
  followUpCount: number;
}

interface ActiveTabDef {
  id: TabType;
  label: string;
  icon: React.ReactNode;
  countKey?: 'emailCount' | 'socialCount' | 'followUpCount';
  disabled?: false;
}

interface ComingSoonTabDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  disabled: true;
}

type TabDef = ActiveTabDef | ComingSoonTabDef;

const tabs: TabDef[] = [
  { id: 'script', label: 'Webinar Script', icon: <FileText className="w-4 h-4" /> },
  { id: 'calendar', label: 'Marketing Calendar', icon: <CalendarRange className="w-4 h-4" /> },
  { id: 'emails', label: 'Marketing Emails', icon: <Mail className="w-4 h-4" />, countKey: 'emailCount' },
  { id: 'social', label: 'Social Posts', icon: <MessageSquare className="w-4 h-4" />, countKey: 'socialCount' },
  { id: 'followup', label: 'Follow-Up', icon: <Send className="w-4 h-4" />, countKey: 'followUpCount' },
  { id: 'landing', label: 'Landing Page', icon: <Globe className="w-4 h-4" /> },
  { id: 'slides', label: 'Slides', icon: <Presentation className="w-4 h-4" />, disabled: true },
  { id: 'video', label: 'Video', icon: <Video className="w-4 h-4" />, disabled: true },
  { id: 'infographic', label: 'Infographic', icon: <Image className="w-4 h-4" />, disabled: true },
];

export function TabNavigation({
  activeTab,
  onTabChange,
  emailCount,
  socialCount,
  followUpCount,
}: TabNavigationProps) {
  const counts: Record<string, number> = { emailCount, socialCount, followUpCount };
  const [hoveredDisabled, setHoveredDisabled] = useState<string | null>(null);

  return (
    <div className="flex flex-wrap gap-2 p-1 rounded-xl bg-slate-800/50 border border-slate-700/50">
      {tabs.map((tab) => {
        if (tab.disabled) {
          return (
            <div key={tab.id} className="relative">
              <button
                disabled
                onMouseEnter={() => setHoveredDisabled(tab.id)}
                onMouseLeave={() => setHoveredDisabled(null)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-slate-600 cursor-not-allowed opacity-50"
              >
                <span className="flex items-center gap-2">
                  {tab.icon}
                  <span className="hidden sm:inline">{tab.label}</span>
                </span>
              </button>
              {hoveredDisabled === tab.id && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg bg-slate-700 text-xs text-slate-300 whitespace-nowrap shadow-lg border border-slate-600 z-50">
                  Coming Soon
                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-slate-700" />
                </div>
              )}
            </div>
          );
        }

        const activeDef = tab as ActiveTabDef;
        const count = activeDef.countKey ? counts[activeDef.countKey] : undefined;
        const isActive = activeTab === activeDef.id;

        return (
          <button
            key={activeDef.id}
            onClick={() => onTabChange(activeDef.id)}
            className={`relative flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              isActive
                ? 'text-white'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
            }`}
          >
            {isActive && (
              <motion.div
                layoutId="activeTab"
                className="absolute inset-0 bg-primary-600/30 border border-primary-500/30 rounded-lg"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              />
            )}
            <span className="relative flex items-center gap-2">
              {activeDef.icon}
              <span className="hidden sm:inline">{activeDef.label}</span>
              {count !== undefined && (
                <span
                  className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold rounded-full leading-none tabular-nums ${
                    isActive ? 'bg-white/20 text-white' : 'bg-slate-700/70 text-slate-200'
                  }`}
                >
                  {count}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
